// src/pages/Signup.jsx
import React, { useEffect, useMemo, useState } from "react";
import Meta from "../components/Meta";
import Custominput from "../components/Custominput";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { IoArrowBack } from "react-icons/io5";
import { FiEye, FiEyeOff } from "react-icons/fi";

import kuptoLogo from "../images/logo/kupto logobb-8.png";
import "./styles/signup.css";

// ✅ simple rules (same style as screenshot)
const hasUpper = (s) => /[A-Z]/.test(s);
const hasLower = (s) => /[a-z]/.test(s);
const hasNum = (s) => /[0-9]/.test(s);
const hasSpecial = (s) => /[^A-Za-z0-9]/.test(s);
const minLen = (s, n = 8) => (s || "").length >= n;

const isValidEmail = (v) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || "").trim());

const normalizeIdentity = (raw) => {
  const v = String(raw || "").trim();
  if (!v) return { identity: "", type: "" };
  if (isValidEmail(v)) return { identity: v.toLowerCase(), type: "email" };
  const cleaned = v.replace(/[^\d+]/g, "");
  return { identity: cleaned, type: "phone" };
};

const clamp = (n, a, b) => Math.max(a, Math.min(b, n));

const Signup = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ from VerificationCode.jsx
  const stateIdentity = location?.state?.identity || "";
  const stateType = location?.state?.type || "";
  const verified = Boolean(location?.state?.verified);

  // ✅ preserve returnTo
  const returnToFromState = location?.state?.returnTo;
  const returnTo = useMemo(() => {
    if (typeof returnToFromState === "string" && returnToFromState.trim()) {
      return returnToFromState;
    }
    try {
      const ss = sessionStorage.getItem("kupto_returnTo");
      if (ss && ss.trim()) return ss;
    } catch {}
    return "/";
  }, [returnToFromState]);

  const { identity, type } = useMemo(() => {
    const n = normalizeIdentity(stateIdentity);
    return {
      identity: stateIdentity ? n.identity : "",
      type: stateType || n.type || "",
    };
  }, [stateIdentity, stateType]);

  useEffect(() => {
    // ✅ FIX: your route is /identification (NOT /identify)
    if (!identity) {
      navigate("/identification", { replace: true });
      return;
    }

    // force verification before signup
    if (identity && !verified) {
      navigate("/verification-code", {
        state: { identity, type, returnTo },
        replace: true,
      });
    }
  }, [identity, verified, navigate, type, returnTo]);

  useEffect(() => {
    try {
      if (returnTo && returnTo !== "/") {
        sessionStorage.setItem("kupto_returnTo", returnTo);
      }
    } catch {}
  }, [returnTo]);

  const maskedIdentity = useMemo(() => {
    if (!identity) return "";
    if (type === "email") {
      const [u, d] = identity.split("@");
      if (!u || !d) return identity;
      const head = u.slice(0, 1);
      return `${head}${"*".repeat(Math.max(1, u.length - 1))}@${d}`;
    }
    const digits = identity.replace(/[^\d]/g, "");
    if (digits.length < 4) return identity;
    return `***${digits.slice(-3)}`;
  }, [identity, type]);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [showPw, setShowPw] = useState(false);
  const [showCp, setShowCp] = useState(false);

  const [touched, setTouched] = useState({ pw: false, cp: false });
  const [apiErr, setApiErr] = useState("");

  const rules = useMemo(() => {
    const p = password || "";
    return {
      min8: minLen(p, 8),
      upper: hasUpper(p),
      lower: hasLower(p),
      num: hasNum(p),
      special: hasSpecial(p),
    };
  }, [password]);

  const allRulesOk =
    rules.min8 && rules.upper && rules.lower && rules.num && rules.special;

  const passwordsMatch =
    password.length > 0 &&
    confirmPassword.length > 0 &&
    password === confirmPassword;

  const showPwErr =
    (touched.pw && !allRulesOk) || (touched.pw && !password.trim());

  const showCpErr =
    touched.cp &&
    (!confirmPassword.trim() ||
      (password.trim() && confirmPassword !== password));

  const canContinue = allRulesOk && passwordsMatch;

  // ✅ Password progress
  const progress = useMemo(() => {
    const p = password || "";
    const checks = [
      minLen(p, 8),
      hasUpper(p),
      hasLower(p),
      hasNum(p),
      hasSpecial(p),
    ];
    const score = checks.reduce((acc, ok) => acc + (ok ? 1 : 0), 0); // 0..5
    const percent = clamp(Math.round((score / 5) * 100), 0, 100);

    let label = "";
    if (!p) label = "";
    else if (percent <= 20) label = "Weak";
    else if (percent <= 60) label = "Fair";
    else if (percent < 100) label = "Good";
    else label = "Strong";

    return { percent, label };
  }, [password]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setApiErr("");
    setTouched({ pw: true, cp: true });

    if (!allRulesOk) {
      setApiErr("Please meet all password requirements.");
      return;
    }
    if (!passwordsMatch) {
      setApiErr("Passwords must match.");
      return;
    }

    // ✅ IMPORTANT:
    // This navigation relies on location.state.
    // If OpenRoutes redirects /signup away, this state won't be passed.
    // After our earlier fixes, /signup should render normally, so this will work.
    navigate("/profile-details", {
      replace: true,
      state: {
        identity,
        type,
        verified: true,
        returnTo,
        password,
      },
    });
  };

  const backReturnTo = useMemo(() => {
    try {
      return returnTo || sessionStorage.getItem("kupto_returnTo") || "/";
    } catch {
      return returnTo || "/";
    }
  }, [returnTo]);

  return (
    <>
      <Meta title="Sign up" />

      <div className="ku-su-wrap">
        <div className="ku-su-shell">
          <div className="ku-su-split">
            <button
              type="button"
              className="ku-su-back"
              onClick={() => navigate(backReturnTo)}
              aria-label="Go back"
            >
              <IoArrowBack />
            </button>

            {/* LEFT */}
            <div className="ku-su-left">
              <div className="ku-su-brand" aria-hidden="true">
                <div className="ku-su-mark">
                  <img
                    src={kuptoLogo}
                    alt="Kupto"
                    className="ku-su-logo"
                    draggable="false"
                  />
                </div>
              </div>

              <div className="ku-su-head">
                <h1 className="ku-su-title">Create account</h1>
                <p className="ku-su-sub">
                  Creating account for <b>{maskedIdentity}</b>{" "}
                  <Link
                    className="ku-su-miniLink"
                    to="/identification"
                    state={{ returnTo }}
                  >
                    Change
                  </Link>
                </p>
              </div>

              <form className="ku-su-form" onSubmit={onSubmit} noValidate>
                <div className="ku-su-field">
                  <div
                    className={`ku-su-input ku-su-input--pw ${
                      showPwErr ? "is-error" : ""
                    }`}
                  >
                    <Custominput
                      type={showPw ? "text" : "password"}
                      name="password"
                      placeholder="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      onBlur={() => setTouched((s) => ({ ...s, pw: true }))}
                    />
                    <button
                      type="button"
                      className="ku-su-eyeBtn"
                      onClick={() => setShowPw((s) => !s)}
                      aria-label={showPw ? "Hide password" : "Show password"}
                    >
                      {showPw ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>

                  {/* ✅ Progress row (NOW VISIBLE ON MOBILE) */}
                  {password ? (
                    <div
                      className="ku-su-progressRow"
                      aria-label="Password strength"
                    >
                      <div className="ku-su-progressTrack">
                        <div
                          className="ku-su-progressBar"
                          style={{ width: `${progress.percent}%` }}
                        />
                      </div>
                      <span className="ku-su-progressLabel">
                        {progress.label}
                      </span>
                    </div>
                  ) : null}
                </div>

                <div className="ku-su-field">
                  <div
                    className={`ku-su-input ku-su-input--pw ${
                      showCpErr ? "is-error" : ""
                    }`}
                  >
                    <Custominput
                      type={showCp ? "text" : "password"}
                      name="confirmPassword"
                      placeholder="Confirm password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      onBlur={() => setTouched((s) => ({ ...s, cp: true }))}
                    />
                    <button
                      type="button"
                      className="ku-su-eyeBtn"
                      onClick={() => setShowCp((s) => !s)}
                      aria-label={
                        showCp ? "Hide password" : "Show password"
                      }
                    >
                      {showCp ? <FiEyeOff /> : <FiEye />}
                    </button>
                  </div>

                  {confirmPassword.trim() ? (
                    <div
                      className={`ku-su-match ${
                        passwordsMatch ? "ok" : "bad"
                      }`}
                    >
                      {passwordsMatch
                        ? "Passwords match"
                        : "Passwords do not match"}
                    </div>
                  ) : null}
                </div>

                {apiErr ? <div className="ku-su-error">{apiErr}</div> : null}

                <button
                  type="submit"
                  className="ku-su-btn"
                  disabled={!canContinue}
                >
                  Continue
                </button>

                <p className="ku-su-legal">
                  By continuing you agree to Kupto’s{" "}
                  <Link to="/terms-and-conditions">Terms and Conditions</Link>
                </p>
              </form>
            </div>

            {/* RIGHT (desktop only) */}
            <div className="ku-su-right ku-su-right--desktopOnly">
              <h3 className="ku-su-rightTitle">Password Requirements</h3>

              <div className="ku-su-reqCard">
                <ul className="ku-su-reqList">
                  <li className={rules.min8 ? "ok" : ""}>
                    Password must be at least 8 characters long
                  </li>
                  <li className={rules.upper ? "ok" : ""}>
                    Password must contain at least one uppercase letter (A-Z)
                  </li>
                  <li className={rules.lower ? "ok" : ""}>
                    Password must contain at least one lowercase letter (a-z)
                  </li>
                  <li className={rules.num ? "ok" : ""}>
                    Password must contain at least one number (0-9)
                  </li>
                  <li className={rules.special ? "ok" : ""}>
                    Password must contain at least one special character
                  </li>
                </ul>
              </div>

              <div className="ku-su-tip">
                <div className="ku-su-tipIcon">🔒</div>
                <div className="ku-su-tipTxt">
                  <p className="ku-su-tipH">Keep it secure</p>
                  <p className="ku-su-tipP">
                    Never share your password with anyone.
                  </p>
                </div>
              </div>

              <div className="ku-su-smallCard">
                <p className="ku-su-smallTitle">Already have an account?</p>
                <p className="ku-su-smallText">Go back and sign in instead.</p>
                <Link
                  to="/login"
                  state={{ identity, type, returnTo }}
                  className="ku-su-smallBtn"
                >
                  Go to Sign in
                </Link>
              </div>
            </div>
            {/* END RIGHT */}
          </div>
        </div>
      </div>
    </>
  );
};

export default Signup;